// Modal.js
import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import { getOrdersById } from "../services/order";
import moment from "moment";
import styles from "../shared/table/table.module.scss";

const DeviceOrderModal = ({ id, isOpen, onClose }: any) => {
  const [viewOrders, setViewOrders] = useState<any>();
  const [showImage, setShowImage] = useState(false);
  const [deviceImage, setDeviceImage] = useState<string>();
  const [loading, setLoading] = useState(false);

  const getOrderByIdFunc = useCallback(async () => {
    if (!id?.order_id) return;

    setLoading(true);
    try {
      const orderIdObj = {
        userId: id?.customerId,
        orderId: Number(id?.order_id),
      };

      const getOrders = await getOrdersById(orderIdObj);
      setViewOrders(getOrders);
    } catch (error) {
      console.error("Error fetching order:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const showImageFunc = (image: any) => {
    setShowImage(true);
    setDeviceImage(image);
  };

  useEffect(() => {
    if (id !== undefined && isOpen) {
      getOrderByIdFunc();
    }
  }, [id, isOpen, getOrderByIdFunc]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setViewOrders(undefined);
    }
  }, [isOpen]);

  const handleClose = () => {
    setShowImage(false);
    onClose();
  };

  const order = viewOrders?.order;
  const shipping = order?.Shippings?.[0];
  const payment = order?.Payments?.[0];

  return (
    <>
      <Modal
        size="xl"
        show={isOpen}
        onHide={handleClose}
        backdrop={showImage ? "static" : true}
        keyboard={!showImage}
        centered
        className="device-order-modal"
      >
        <Modal.Header closeButton style={{ borderBottom: '1px solid var(--border-color)' }}>
          <Modal.Title style={{ fontSize: '1.25rem', fontWeight: 600 }}>
            Device Order Details {order?.id ? `#${order.id}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.5rem' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <span>Loading order details...</span>
            </div>
          ) : order ? (
            <>
              {/* Order Summary */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: 'var(--bg-tertiary)',
                borderRadius: '8px'
              }}>
                <div>
                  <strong>Customer Email:</strong>
                  <p style={{ margin: 0 }}>{order.email || '-'}</p>
                </div>
                <div>
                  <strong>Total Price:</strong>
                  <p style={{ margin: 0 }}>₹{order.soldPrice || order.totalPrice}</p>
                </div>
                <div>
                  <strong>Payment Method:</strong>
                  <p style={{ margin: 0 }}>{payment?.paymentMethod || "-"}</p>
                </div>
                <div>
                  <strong>Transaction ID:</strong>
                  <p style={{ margin: 0 }}>{payment?.transactionId || "-"}</p>
                </div>
                <div>
                  <strong>Bank Reference:</strong>
                  <p style={{ margin: 0 }}>{payment?.bankReference || "-"}</p>
                </div>
                
                <div>
                  <strong>Order Date:</strong>
                  <p style={{ margin: 0 }}>{order.createdAt ? moment(order.createdAt).format("DD-MM-YYYY") : '-'}</p>
                </div>
              </div>

              {/* Shipping Details */}
              {shipping && (
                <div style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  backgroundColor: 'var(--bg-tertiary)',
                  borderRadius: '8px'
                }}>
                  <h6 style={{ marginBottom: '0.75rem', fontWeight: 600 }}>Shipping Details</h6>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
                    <p style={{ margin: 0 }}><strong>Name:</strong> {shipping.firstName} {shipping.lastName}</p>
                    <p style={{ margin: 0 }}><strong>Phone:</strong> {shipping.phoneNumber}</p>
                    <p style={{ margin: 0 }}><strong>Email:</strong> {shipping.emailId}</p>
                    <p style={{ margin: 0 }}><strong>Address:</strong> {shipping.flatNumber ? `${shipping.flatNumber}, ` : ''}{shipping.address}</p>
                    <p style={{ margin: 0 }}><strong>City:</strong> {shipping.city}</p>
                    <p style={{ margin: 0 }}><strong>State:</strong> {shipping.state}</p>
                    <p style={{ margin: 0 }}><strong>Pincode:</strong> {shipping.zipcode}</p>
                    <p style={{ margin: 0 }}><strong>Country:</strong> {shipping.country}</p>
                  </div>
                </div>
              )}

              {/* Order Items Table */}
              <h6 style={{ marginBottom: '0.75rem', fontWeight: 600 }}>Order Items</h6>
              <div style={{ overflowX: 'auto' }}>
                <table className="table" style={{ marginBottom: '1.5rem' }}>
                  <thead>
                    <tr>
                      <th scope="col" className={styles.table_title}>Product ID</th>
                      <th scope="col" className={styles.table_title}>Custom Name</th>
                      <th scope="col" className={styles.table_title}>Original Price</th>
                      <th scope="col" className={styles.table_title}>Selling Price</th>
                      <th scope="col" className={styles.table_title}>Quantity</th>
                      {viewOrders?.deviceImages?.length > 0 && (
                        <th scope="col" className={styles.table_title}>
                          Image
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {order?.OrderBreakDowns?.map((val: any, index: number) => (
                      <tr key={index}>
                        <td className={styles.table_data}>{val.productId}</td>
                        <td className={styles.table_data}>{val.customName || "-"}</td>
                        <td className={styles.table_data}>₹{val.originalPrice}</td>
                        <td className={styles.table_data}>₹{val.sellingPrice}</td>
                        <td className={styles.table_data}>{val.quantity}</td>
                        {viewOrders?.deviceImages?.length > 0 && (
                          <td>
                            {viewOrders?.deviceImages?.[index] ? (
                              <img
                                src={viewOrders?.deviceImages[index]}
                                alt="Product"
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                                onClick={() => showImageFunc(viewOrders?.deviceImages[index])}
                              />
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <span>No order data available</span>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid var(--border-color)', padding: '1rem 1.5rem' }}>
          <Button
            variant="outline-secondary"
            onClick={handleClose}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontWeight: 500,
              transition: 'all 0.2s ease'
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Image Preview Modal */}
      {showImage && (
        <Modal
          size="lg"
          show={showImage}
          onHide={() => setShowImage(false)}
          centered
          style={{ zIndex: 1060 }}
        >
          <Modal.Header closeButton>
            <Modal.Title>Device Image</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ textAlign: 'center', padding: '2rem' }}>
            <img
              alt="Device preview"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: 'contain',
                borderRadius: '8px'
              }}
              src={deviceImage}
            />
          </Modal.Body>
          <Modal.Footer style={{ borderTop: '1px solid var(--border-color)', padding: '1rem 1.5rem' }}>
            <Button
              variant="outline-secondary"
              onClick={() => setShowImage(false)}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontWeight: 500,
                transition: 'all 0.2s ease'
              }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default DeviceOrderModal;
